import { useLocation, useNavigate } from "react-router-dom";
import CustomDataGrid from "../../components/dms_common/CustomDataGrid";
import GenericFormHeader from "../../components/dms_common/GenericFormHeader";
import "./viewSaleReturnDetails.css"
import { useCallback, useEffect, useState, useMemo } from "react";
import { BatchDetailsMap, Invoice, SalesBatchId, SalesDetail, SalesReasonDetails } from "../../types/types";
import { openPopup } from "../../stateManagement/actions/popupActions";
import { getBatchDetailsFromDb, getBatchIdsFromMap, getEditSaleReturnRequest, getProductDetailsFromDb, rejectSalesReturnWithoutInvoice, validateReturnDetailsWithReturn } from "../SalesReturn/SalesReturnService";
import Header from "../../components/dms_common/Header";
import CustomAccordian from "../../components/dms_common/CustomAccordian";
import { salesReturnStatusMapping } from "../SalesReturn/SalesReturnUtils";
import { CreditNote } from "../../components/creditNote/creditNote";
import { getCreditAmount } from "../deliveryConfirmation/DeliveryConfirmationService";
import { Loader } from "../../components/loader/Loader";
import { logLoadTrace } from "../../firebase";
import SaleReturnRejectReasonModal from "./saleReturnRejectReasonModal";
import SaleReturnApprovalModal from "./saleReturnApprovalModal";
import moment from "moment";
import { getPieceFilterMapping } from "../createSecondaryOrder/CreateSecondaryOrderService";
import MultiLevelTable from "../../components/multiLevelTable/multiLevelTable";
import { Box } from "@mui/material";
import { KeyboardArrowDown, KeyboardArrowUp } from "@material-ui/icons";
import { getDataFromLocalDb } from "../../services/dbService";
import { Product } from "../createSecondaryOrder/models";
import { getMergedCatalogueAndRealTimeStockData } from "../stocks/RealTimeStock/RealTimeStockUtils";


const ViewSaleReturnWithoutInvoice = () => {

    const navigate = useNavigate();
    const location = useLocation();
    const initialBatchDetails:BatchDetailsMap = location.state.initialBatchDetails;
    const returnData:Invoice = location.state.returnData;
    const [salesDetails, setSalesDetails] = useState<SalesDetail[]>([]);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(true);
    const [disableSubmit, setDisableSubmit] = useState<boolean>(false);
    const [openCreditNote, setOpenCreditNote] = useState<boolean>(false);
    const [expanded, setExpanded] = useState<boolean>(true);
    const [totalCreditAmount, setTotalCreditAmount] = useState<number>(0);
    const [status, setStatus] = useState<string>(returnData.status);
    const [modalType, setModalType] = useState<string>("");
    const headerLabel = location.state.headerLabel;
    const [subTableRowIds, setSubTableRowIds] = useState<string[]>([]);
    const ConfigDictionary = JSON.parse(localStorage.getItem("ConfigDictionary") || "{}");
    const { otherUnitLabel } = ConfigDictionary;


    useEffect(()=>{
        const dataFunction = async ()=>{
            const productsData = await getDataFromLocalDb("Product");
                    const productsMap: Record<string, Product> = productsData?.data?.[0]?.data ?? {};
                const mergedData = getMergedCatalogueAndRealTimeStockData(
                    returnData.salesDetails,
                    productsMap
                );
            returnData.salesDetails= mergedData
        }
        
        dataFunction()
    },[])

    // Keyboard shortcuts effect
    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            // Check if modals are open or if we're not in pending status
            if (modalType || openCreditNote || !isPending() || isSubmitting) {
                return;
            }

            const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
            const isCtrlOrCmd = isMac ? event.metaKey : event.ctrlKey;

            if (isCtrlOrCmd) {
                if (event.key.toLowerCase() === 's') {
                    event.preventDefault();
                    handleApproveReturn();
                } else if (event.key.toLowerCase() === 'k') {
                    event.preventDefault();
                    setModalType("reject");
                }
            }
        };

        // Add event listener
        document.addEventListener('keydown', handleKeyDown);

        // Cleanup
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [modalType, openCreditNote, isSubmitting, status, disableSubmit, salesDetails]);

    const totalProducts = useMemo(() => {
        const totals = salesDetails.reduce(
            (acc, detail) => {
                acc.totalCaseQty += detail.caseQuantity || 0;
                acc.totalPieceQty += detail.initialPieceQuantity || 0;
                acc.totalOtherQty += detail.otherUnitQuantity || 0;
                return acc;
            },
            { totalCaseQty: 0, totalPieceQty: 0, totalOtherQty: 0 }
        );

        const parts = [];
        if (totals.totalCaseQty > 0) parts.push(`${totals.totalCaseQty} Cs`);
        if (totals.totalOtherQty > 0 && otherUnitLabel) parts.push(`${totals.totalOtherQty} ${otherUnitLabel}`);
        if (totals.totalPieceQty > 0) parts.push(`${totals.totalPieceQty} Pc`);
        
        return parts.length > 0 ? parts.join(', ') : '0';
    }, [salesDetails, otherUnitLabel]);

    if(!initialBatchDetails){
        navigate(-1);
    }

    const fetchAndUpdateSalesDetails=async()=>{
        try{
            const batchDetails = await getBatchDetailsFromDb();
            const productData = await getProductDetailsFromDb();
            const salesDetailsMap:any = {};
            batchDetails && returnData.salesDetails.forEach(salesDetails=>{
                let caseQty=0, pieceQty =0, otherQty = 0;
                batchDetails[salesDetails.batchCode] && Object.keys(batchDetails[salesDetails.batchCode]).forEach(batchId=>{
                    batchDetails[salesDetails.batchCode][batchId].forEach(batch=>{
                        caseQty += batch.caseQty;
                        pieceQty += batch.pieceQty;
                        otherQty += batch.otherQty;
                    })
                })
                salesDetails.caseQuantity = caseQty;
                salesDetails.initialPieceQuantity = pieceQty;
                salesDetails.mrp = productData[salesDetails.batchCode].mrp;
                salesDetails.otherUnitQuantity = otherQty;
                salesDetails.skuName = productData[salesDetails.batchCode].skuDescription;
                salesDetails.batchIdCount = Object.keys(batchDetails[salesDetails.batchCode] ?? {}).length;
                salesDetails.packType = getPieceFilterMapping(productData[salesDetails.batchCode]?.pieceSize || "");
                salesDetails.batchIds = batchDetails && batchDetails[salesDetails.batchCode] ? getBatchIdsFromMap(initialBatchDetails[salesDetails.batchCode]) : [];
                salesDetails.batchIds?.forEach(batchId=>{
                    batchId.reasonDetails = batchDetails[salesDetails.batchCode][batchId.batchId];
                })
                salesDetails.batchIdDetails = salesDetails.batchIds?.map((batchId: SalesBatchId) => {
                    return batchId.reasonDetails?.map((reasonDetail: SalesReasonDetails) => {
                      return {
                        batchId: batchId.batchId,
                        caseQty: reasonDetail.caseQty,
                        otherQty: reasonDetail.otherQty,
                        pieceQty: reasonDetail.pieceQty,
                        reason: reasonDetail.reason,
                        mrp: salesDetails.mrp
                      }
                    }).flat() ?? [];
                  }).flat() ?? [];
                salesDetails.netAmount = Math.abs(salesDetails.netAmount);
                if(!isApproved()){
                    salesDetails.price = productData[salesDetails.batchCode].price
                    if(!productData[salesDetails.batchCode].price){
                        setDisableSubmit(true);
                    }
                }
                if(salesDetailsMap[salesDetails.batchCode]){
                    const salesDetailsObject = salesDetailsMap[salesDetails.batchCode];
                    salesDetailsObject.netAmount += salesDetails.netAmount;
                }else{
                    salesDetailsMap[salesDetails.batchCode] = salesDetails;
                }
            })
            returnData.salesDetails = Object.values(salesDetailsMap);
            setSalesDetails(returnData.salesDetails);
            if(isApproved()){
                await fetchAndSetCreditAmount();
            }
        }catch(e){}
        setIsSubmitting(false);
    }

    useEffect(()=>{
        logLoadTrace(fetchAndUpdateSalesDetails,"view_sale_returns_page_load_time");
    },[])

    const headerDetails = [
        {
            label:headerLabel || "Sale Returns",
            redirectFunction: ()=>{navigate(-1)}
        },
        {
            label:"Review"
        }
    ]

    const handleRedirect = async (redirectUrl: string, rowData: SalesDetail) => {
        const productDetails = await getProductDetailsFromDb();
        if(!productDetails || !productDetails[rowData.batchCode]){
          openPopup("Error", "Product details not found");
          return;
        }
        const batchDetails = await getBatchDetailsFromDb();
        navigate(redirectUrl, {state:{
          rowData:rowData,
          productDetails:productDetails[rowData.batchCode],
          showReturnQty:true,
          batchDetails:(batchDetails && batchDetails[rowData.batchCode]) ?? {}
        }});
      };

    const handleSearch = useCallback((searchStr: string) => {
        if (!returnData?.salesDetails || returnData.salesDetails.length === 0) {
            setSalesDetails([]);
            return;
        }
        const filteredData = returnData.salesDetails.filter(
            (salesDetail: SalesDetail) =>
                salesDetail.batchCode?.toLowerCase().includes(searchStr.toLowerCase()) ||
                salesDetail.skuName?.toLowerCase().includes(searchStr.toLowerCase()) ||
                salesDetail.skuCode?.toLowerCase().includes(searchStr.toLowerCase()) ||
                salesDetail.packType?.toLowerCase().includes(searchStr.toLowerCase())
        );
        setSalesDetails(filteredData);
    },[])

    const isPending = ()=>{
        return returnData.status.toLowerCase() == "pending";
    }

    const isApproved = ()=>{
        return returnData.status.toLowerCase() == "approved" || returnData.status.toLowerCase() == "credit note issued";
    }


    const isReturnEditted = ()=>{
        let isEditted = false;
        returnData.salesDetails.forEach(salesDetail=>{
            salesDetail.batchIds?.forEach(batchIdObject=>{
                const initialData = initialBatchDetails[salesDetail.batchCode][batchIdObject.batchId];
                if(initialData.length!=batchIdObject.reasonDetails?.length){
                    isEditted = true;
                    return;
                }
                batchIdObject.reasonDetails?.forEach(reasonDetail=>{
                    const intialReasonObject = initialData.find(initialBatch=>initialBatch.reason == reasonDetail.reason);
                    if(!intialReasonObject || reasonDetail.caseQty != intialReasonObject.caseQty || reasonDetail.pieceQty != intialReasonObject.pieceQty || reasonDetail.otherQty != intialReasonObject.otherQty ){
                        isEditted = true;
                    }
                })
            })
        })
        return isEditted;
    }

    const fetchAndSetCreditAmount = async()=>{
        const creditAmount = await getCreditAmount(returnData.invoiceNumber);
        setTotalCreditAmount(creditAmount);
    }
    const getUnsaleableData = (reqBody:any) => {
        return reqBody.salesDetails.filter((detail:any)=>detail.saleCondition == "Unsaleable");
    }
    const handleApproveReturn = async() => {
        const ConfigDictionary = JSON.parse(localStorage.getItem("ConfigDictionary") || "{}");
        const reasonMap = ConfigDictionary?.salesReturnReasonsMap ?? {};
        if(disableSubmit){
            openPopup("Error", "Please enter price for all the products");
            return;
        }
        setIsSubmitting(true);
        try{
            returnData.salesDetails = salesDetails;
            const invoiceDetails = await validateReturnDetailsWithReturn(returnData); 
            const reqBody = await getEditSaleReturnRequest(returnData);
            // const unsaleableData = Object.keys(reasonMap).length != 0 ? getUnsaleableData(reqBody) : undefined;
            const submission = Object.keys(reasonMap).length != 0 ? "newPartialApproveReturn" : isReturnEditted() ? "partialApproveReturn" : "approveReturn";
            navigate("review",{
                state:{
                    invoiceDetails,
                    isReturnInvoice:true,
                    reqBody,
                    // unsaleableData,
                    submission
                }
            })
            return;
        }catch(e){
            openPopup("Error", "Unable to approve sale return, please try again");
        }
        setIsSubmitting(false);
    }
    const handleRejectReturn = async(reason:string) => {
        if(isSubmitting){
            return;
        }
        setIsSubmitting(true);
        try{
            const response = await rejectSalesReturnWithoutInvoice(returnData,reason);
            if(response.status == 200){
                openPopup("Success", "Return rejected successfully",()=>{navigate(-1);});
            }
        }catch(e){
            openPopup("Error", "Unable to reject sale return, please try again");
        }
        setIsSubmitting(false);
    }   

    const getFooterElement = () => {
        return (
            <div className="view-sale-return-submit-button-container">
                {!isSubmitting && isApproved() && returnData.saleCondition?.toLowerCase() != "unsaleable" && returnData.netAmount-totalCreditAmount > 0 && returnData.status.toLowerCase() != "credit note issued" && (
                    <button
                        className={`view-sale-return-submit ${isSubmitting ? "disabled" : ""}`}
                        onClick={()=>{setOpenCreditNote(true)}}
                    >
                        <span className="view-sale-return-submit-text">
                            Issue Credit Note
                        </span>
                    </button>
                )}
                {!isSubmitting && returnData.status.toLowerCase() == "pending" && (
                    <>
                        <button
                            className={`view-sale-return-submit`}
                            onClick={handleApproveReturn}
                            title="Ctrl+S / ⌘+S to Approve"
                        >
                            <span className="view-sale-return-submit-text">
                                Approve
                            </span>
                        </button>
                        <button
                            className={`view-sale-return-submit reject`}
                            onClick={()=>{setModalType("reject")}}
                            title="Ctrl+K / ⌘+K to Reject"
                        >
                            <span className="view-sale-return-submit-text">
                                Reject
                            </span>
                        </button>
                    </>
                )}
                {isSubmitting && (
                    <div style={{ width: "40px", marginRight: "40px" }}>
                        <Loader />
                    </div>
                )}
            </div>
        )
    }

    const getOutletCordianData = ()=>{
        return {
            outletCode: returnData.outletCode,
            outletName: returnData.outletName,
            accountType: returnData.accountType
        }
    }

    const getInvoiceCordianData = ()=>{
        const data: any = {
            returnDate: moment(returnData.creationTime).format("DD-MM-YYYY"),
            totalProducts: totalProducts,
        };
        
        // Only include return value if status is not PENDING
        if (!isPending()) {
            data.returnValue = returnData.netAmount ?? "-";
        }
        
        return data;
    }

    const getInvoiceCordianMetaData = () => {
        const baseMetaData = [
            {
                keys: "returnDate",
                label: "Return Date",
                index: 1,
            },
            {
                keys: "totalProducts",
                label: "Total Products",
                index: 2,
            }
        ];

        // Only include return value metadata if status is not PENDING
        if (!isPending()) {
            baseMetaData.push({
                keys: "returnValue",
                label: "Return Value",
                index: 3,
                valueType: "number",
            } as any);
        }

        return baseMetaData;
    }

     useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (modalType || openCreditNote || document.querySelector('[role="dialog"]')) {
                console.log("Ignoring shortcut due to open modal or credit note");
                return;
            }

            const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
            const isCtrlOrCmd = isMac ? event.metaKey : event.ctrlKey;

            if (isCtrlOrCmd) {
                if (event.key.toLowerCase() === 'a') {
                    event.preventDefault(); // Prevent browser's default select all
                    if (isSubmitting) {
                        console.log("Ignoring shortcut: submission in progress");
                        return;
                    }
                    if (isApproved() && returnData.netAmount - totalCreditAmount > 0) {
                        console.log("Executing setOpenCreditNote for Ctrl + A");
                        logLoadTrace(() => setOpenCreditNote(true), "view_sale_return_without_invoice_issue_credit_note_time");
                    } else if (returnData.status.toLowerCase() === "pending" && !disableSubmit) {
                        console.log("Executing handleApproveReturn for Ctrl + A");
                        logLoadTrace(handleApproveReturn, "view_sale_return_without_invoice_approve_time");
                    }
                } else if (event.key.toLowerCase() === 'x') {
                    event.preventDefault(); // Prevent browser's default cut
                    if (isSubmitting) {
                        console.log("Ignoring shortcut: submission in progress");
                        return;
                    }
                    if (returnData.status.toLowerCase() === "pending") {
                        console.log("Executing setModalType('reject') for Ctrl + X");
                        logLoadTrace(() => setModalType("reject"), "view_sale_return_without_invoice_reject_time");
                    }
                } else if (event.key.toLowerCase() === 's') {
                    event.preventDefault();
                    if (isSubmitting || disableSubmit) {
                        console.log("Ignoring shortcut: submission disabled or in progress");
                        return;
                    }
                    if (returnData.status.toLowerCase() === "pending") {
                        console.log("Executing handleApproveReturn for Ctrl + S");
                        logLoadTrace(handleApproveReturn, "view_sale_return_without_invoice_approve_time");
                    }
                } else if (event.key.toLowerCase() === 'k') {
                    event.preventDefault();
                    if (isSubmitting) {
                        console.log("Ignoring shortcut: submission in progress");
                        return;
                    }
                    if (returnData.status.toLowerCase() === "pending") {
                        console.log("Executing setModalType('reject') for Ctrl + K");
                        logLoadTrace(() => setModalType("reject"), "view_sale_return_without_invoice_reject_time");
                    }
                }
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [modalType, openCreditNote, isSubmitting, status, disableSubmit, salesDetails, returnData, totalCreditAmount, handleApproveReturn]);

    const handleOpenSubTable = (rowId: string) => {
        if (subTableRowIds.includes(rowId)) {
            setSubTableRowIds(subTableRowIds.filter(id => id != rowId));
        } else {
            setSubTableRowIds([...subTableRowIds, rowId]);
        }
    }
    const isSubTableOpen = (rowId: string) => {
        return subTableRowIds.includes(rowId);
    }
    
    const handleRedirectFunction = async (rowData: SalesDetail) => {
        const productDetails = await getProductDetailsFromDb();
        if(!productDetails || !productDetails[rowData.batchCode]){
          openPopup("Error", "Product details not found");
          return;
        }
        const batchDetails = await getBatchDetailsFromDb();
        navigate("edit", {state:{
          rowData:rowData,
          productDetails:productDetails[rowData.batchCode],
          showReturnQty:true,
          batchDetails:(batchDetails && batchDetails[rowData.batchCode]) ?? {}
        }});
      };

    return (
        <div className="view-sale-return-details-container">
            <GenericFormHeader
                labelDetails={headerDetails}
                searchFunction={handleSearch}
            />
            <CustomAccordian
                expanded={expanded}
                onCustomAccordionToggle={()=>{setExpanded(!expanded)}}
                header={`Return Order No. ${returnData.invoiceNumber}`}
                status={status}
                statusConfig={salesReturnStatusMapping} 
                metaData1={outletCordianMetaData}
                data1={getOutletCordianData()}
                metaData2={getInvoiceCordianMetaData()}
                data2={getInvoiceCordianData()}
                label1="Outlet Info"
                label2="Return Info"
                isSecondHalf={true}
                singleColumnInEachHalf={false}
            />
            <Header
                title={ isPending() ? "Review Return Qty" : "Approved Return Qty"}
            />
            {/* {isPending() ?
                <CustomDataGrid
                    rows={salesDetails}
                    columnsMetaData={getSalesReturnDetailsColumnMetaData()}
                    tableWidth={0.78}
                    redirectUrl={"edit"}
                    redirectUrlFunction={handleRedirect}
                    footerElement={getFooterElement}
                    enableRowClickRedirect={isPending() ? true : false}
                /> : */}
                <MultiLevelTable
                    rowData={salesDetails}
                    columnsMetaData={getViewSalesReturnDetailsColumnMetaData(handleOpenSubTable, isSubTableOpen, isPending(), handleRedirectFunction)}
                    footerElement={getFooterElement}
                    subTableRowIds={subTableRowIds}
                    subTableMetaData={subTableMetaData}
                />
            
            <CreditNote open={openCreditNote} setOpen={setOpenCreditNote} outletCode={returnData.outletCode} returnId={returnData.invoiceNumber} returnAmount={returnData.netAmount ?? 0} creditAmount={totalCreditAmount}/>
            <SaleReturnRejectReasonModal open={modalType == "reject"} closeModal={()=>{setModalType("")}} onClick={handleRejectReturn} />
            <SaleReturnApprovalModal open={modalType == "approve"} closeModal={()=>{setModalType("")}} onClick={()=>{setModalType("");setOpenCreditNote(true)}} />
        </div>
    )
}

const subTableMetaData = {
    mappedValue: "batchIdDetails",
    columns: [
        {
            headerName: "Batch Id",
            mappedValue: "batchId",
            width: "25%",
        },
        {
            headerName: "MRP",
            mappedValue: "mrp",
            width: "20%",
        },
        {
            headerName: "Returned Qty",
            mappedValue: "caseQty,pieceQty,otherQty",
            displayType: "quantity",
            width: "25%",
        },
        {
            headerName: "Reason",
            mappedValue: "reason",
            width: "30%",
        }
    ]
}

const getViewSalesReturnDetailsColumnMetaData = (handleOpenSubTable: (rowId: string) => void, isSubTableOpen: (rowId: string) => boolean, isPending: boolean, handleRedirect: (rowData: SalesDetail) => void) => {
    const metaData = [
        {
            headerName: "SKU Code",
            mappedValue: "batchCode",
            width: isPending ? "17%" : "15%",
        },
        {
            headerName: "SKU Name",
            mappedValue: "skuName",
            width: isPending ? "22%" : "20%",
        },
        {
            headerName: "Pack Type",
            mappedValue: "packType",
            width: isPending ? "14%" : "12%",
        },
        {
            headerName: "Batch Ids",
            mappedValue: "batchIdCount",
            width: "16%",
            renderCell: (row: SalesDetail) => {
                return <Box
                className="batch-drowdown-container"
                onClick={() => handleOpenSubTable(row.id)}
                sx={{
                  cursor: "pointer",
                  marginLeft: "auto",
                  marginRight: "auto",
                  backgroundColor: "#F7F7F7",
                }}
              >
                <Box sx={{ color: isSubTableOpen(row.id) ? "#17B4A3" : "#909090", fontSize: "11px" }}>
                  View all batch IDs
                </Box>
                <div
                  className="icon-border-circle"
                  style={{ borderColor: isSubTableOpen(row.id) ? "#17B4A3" : "#909090" }}
                >
                  {isSubTableOpen(row.id) ? (
                    <KeyboardArrowUp
                      style={{
                        fontSize: "13px",
                        color: isSubTableOpen(row.id) ? "#17B4A3" : "#909090",
                      }}
                    />
                  ) : (
                    <KeyboardArrowDown style={{ fontSize: "13px" }} />
                  )}
                </div>
              </Box>
            }
        },
        {
            headerName: "PTR",
            mappedValue: "price",
            width: isPending ? "12%" : "10%",
            displayType: "amount"
        },
        {
            headerName: "Return Qty",
            mappedValue: "caseQuantity,initialPieceQuantity,otherUnitQuantity",
            width: isPending ? "17%" : "15%",
            displayType: "quantity",
            hasEditPenSvg: isPending ? true : false,
            redirectFunction: isPending ? handleRedirect : undefined
        },
        {
            headerName: "Return Value",
            mappedValue: "netAmount",
            width: "12%",
            displayType: "amount"
        },
    ];
    return isPending ? metaData.slice(0, -1) : metaData;
}

const outletCordianMetaData = [
    {
        keys: "outletCode",
        label: "Outlet Code",
        index: 1,
    },
    {
        keys: "outletName",
        label: "Outlet Name",
        index: 2,
    },
    {
        keys: "accountType",
        label: "Account Type",
        index: 3,
    }
    ];


const getSalesReturnDetailsColumnMetaData =()=> ({
    columns: [
        {
            headerName: "SKU Code",
            mappedValue: "batchCode",
            width: 50,
            headerAlign: "left",
            align: "left",
        },
        {
            headerName: "SKU Name",
            mappedValue: "skuName",
            width: 120,
            headerAlign: "left",
            align: "left",
        },
        {
            headerName: "Pack Type",
            mappedValue: "packType",
            width: 40,
            headerAlign: "left",
            align: "left",
        },
        // {
        //     headerName: "Batch Ids",
        //     mappedValue: "batchIdCount",
        //     width: 50,
        //     headerAlign: "center",
        //     align: "center",
        // },
        {
            headerName: "PTR",
            mappedValue: "price",
            width: 40,
            headerAlign: "left",
            align: "left",
            amountDisplay: true,
        },
        {
            headerName: "Return Qty",
            mappedValue: "caseQuantity,initialPieceQuantity,otherUnitQuantity",
            width: 45,
            headerAlign: "left",
            align: "left",
            qtyDisplay: true,
            hasEditPenSvg: true,
            isEditable: true
        },
        // {
        //     headerName: "Return Value",
        //     mappedValue: "netAmount",
        //     amountDisplay: true,
        //     width: 60,
        //     headerAlign: "left",
        //     align: "left",
        // },
    ],
    Actions: [],
});

export default ViewSaleReturnWithoutInvoice;
